import React, {
  Suspense,
  lazy,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import {
  Sparkles,
  X,
  RotateCcw,
  Send,
  Paperclip,
  FileText,
  Image as ImageIcon,
  Loader2,
  ArrowUpRight,
  Download,
  Maximize2,
  Minimize2,
  PanelLeft,
  PanelLeftClose,
  Plus,
  MessageSquare,
  Edit2,
  Trash2,
  Check,
  Bell,
  BellOff,
  Activity,
  FastForward,
  MoreHorizontal,
  Square,
} from "lucide-react";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import {
  getLocalStorageWithExpiry,
  setLocalStorageWithExpiry,
  ReactMarkdownWithGFMPlugin,
  showApiError,
  showSuccess,
  downloadAssistantFile,
} from "@/utils";
import * as C from "@/const";
import { useNotifications } from "@/hooks/useNotifications";
import { formatNotificationTime } from "@/services/notificationService";
import { triggerAiUsageRefresh } from "@/services/aiUsageService";
import { useAuth } from "@/context/AuthContext";
import { ReasoningStepsAccordion } from "../components/assistant/reasoning/ReasoningStepsAccordion";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import "./ai_assistant_sidebar.css";

const GeoStudio = lazy(() => import("../components/assistant/studio/GeoStudio"));

// Official elelem brand logo (see BRANDING.md)
const ELELEM_LOGO_URL = "https://cdn.geo.elelem.ai/logo--240w.png";

const sanitizeAssistantContent = (content) => DOMPurify.sanitize(content || "");

const isTurnLimitMessage = (text) => {
  if (typeof text !== "string") return false;
  return (
    text.includes("limit of 20 turns") ||
    text.includes("maximum turn limit") ||
    text.includes("上限（20ターン）") ||
    text.includes("上限に達しました")
  );
};

const getDefaultStarterSuggestions = (t) => [
  t("assistant.defaultLozenge1", "How easy is it to install AI Mode?"),
  t("assistant.defaultLozenge2", "How is it different to a chatbot?"),
  t("assistant.defaultLozenge3", "Do I need a developer, or is it plug and play?"),
];

const TICK_INTERVAL_MS = 25;
const MAX_TYPEWRITER_DURATION_MS = 1800;
const TARGET_TICKS = 45;

const TypewriterText = ({ id, text, onComplete }) => {
  const { t } = useTranslation();
  const [displayedText, setDisplayedText] = useState("");
  const onCompleteRef = useRef(onComplete);
  const finishedRef = useRef(false);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  const finishImmediately = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setDisplayedText(text || "");
    if (onCompleteRef.current) onCompleteRef.current(id);
  }, [text, id]);

  useEffect(() => {
    finishedRef.current = false;
    const words = (text || "").split(/(\s+)/);
    let idx = 0;
    setDisplayedText("");

    if (words.length === 0) {
      finishImmediately();
      return;
    }

    // Adaptive chunk scaling: finish in ~TARGET_TICKS (~1.1s)
    const chunksPerTick = Math.max(1, Math.ceil(words.length / TARGET_TICKS));

    const interval = setInterval(() => {
      if (idx < words.length) {
        let chunkBatch = "";
        for (let c = 0; c < chunksPerTick && idx < words.length; c++) {
          chunkBatch += words[idx];
          idx++;
        }
        setDisplayedText((prev) => prev + chunkBatch);
      } else {
        clearInterval(interval);
        finishImmediately();
      }
    }, TICK_INTERVAL_MS);

    // Hard time cap: 1.8 seconds maximum duration regardless of text length
    const timeout = setTimeout(() => {
      clearInterval(interval);
      finishImmediately();
    }, MAX_TYPEWRITER_DURATION_MS);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [text, id, finishImmediately]);

  const skipHint = t(
    "assistant.skipAnimation",
    "Click to show full message",
  );

  return (
    <div
      className="elelem-assistant-typewriter-wrapper"
      onClick={(e) => {
        if (e.target.closest("a") || e.target.closest("button")) return;
        finishImmediately();
      }}
      data-testid="copilot-typewriter-wrapper"
    >
      <div className="elelem-assistant-typewriter-header">
        <button
          type="button"
          className="elelem-assistant-skip-typewriter-btn"
          onClick={finishImmediately}
          aria-label={skipHint}
          title={skipHint}
          data-testid="copilot-skip-animation-btn"
        >
          <FastForward size={11} aria-hidden="true" />
          <span>{t("assistant.skip", "Skip")}</span>
        </button>
      </div>
      <div
        className="elelem-assistant-typewriter-content"
        title={skipHint}
      >
        <ReactMarkdownWithGFMPlugin>
          {sanitizeAssistantContent(displayedText)}
        </ReactMarkdownWithGFMPlugin>
      </div>
    </div>
  );
};

function parseServerDate(dateStr) {
  if (!dateStr) return new Date();
  if (dateStr instanceof Date) return dateStr;
  const str = String(dateStr).trim();
  if (!str.endsWith("Z") && !/[+-]\d{2}:?\d{2}$/.test(str)) {
    return new Date(str + "Z");
  }
  return new Date(str);
}

function groupConversationsByDate(conversations, t) {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const isSameDay = (d1, d2) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  const groups = {
    today: [],
    yesterday: [],
    last7Days: [],
    older: [],
  };

  (conversations || []).forEach((conv) => {
    const rawDate = conv.updated_at || conv.created_at;
    const date = parseServerDate(rawDate);
    const diffDays = Math.floor((today - date) / (1000 * 60 * 60 * 24));

    if (isSameDay(date, today)) {
      groups.today.push(conv);
    } else if (isSameDay(date, yesterday)) {
      groups.yesterday.push(conv);
    } else if (diffDays < 7) {
      groups.last7Days.push(conv);
    } else {
      groups.older.push(conv);
    }
  });

  return [
    { key: "today", label: t("assistant.historyToday", "Today"), items: groups.today },
    { key: "yesterday", label: t("assistant.historyYesterday", "Yesterday"), items: groups.yesterday },
    { key: "last7Days", label: t("assistant.historyLast7Days", "Previous 7 Days"), items: groups.last7Days },
    { key: "older", label: t("assistant.historyOlder", "Older"), items: groups.older },
  ].filter((g) => g.items.length > 0);
}

export const triggerElelemAssistantPrompt = (text) => {
  window.dispatchEvent(
    new CustomEvent("elelem-assistant:send-prompt", { detail: text }),
  );
};

export const triggerCoPilotPrompt = (text) => {
  triggerElelemAssistantPrompt(text);
};

// Expose triggers globally so non-React scripts can also use them
window.triggerElelemAssistantPrompt = triggerElelemAssistantPrompt;
window.triggerCoPilotPrompt = triggerCoPilotPrompt;

export default function AIAssistantSidebar() {
  const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    "",
  );
  const { t, i18n } = useTranslation();
  const authContext = useAuth();
  const currentClientId =
    authContext?.user?.client_id || getLocalStorageWithExpiry("client_id");

  const [assistantMode, setAssistantMode] = useState(() => {
    return getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_MODE_KEY) || C.ASSISTANT_MODE_SPOTLIGHT;
  });
  const isSpotlightMode = assistantMode === C.ASSISTANT_MODE_SPOTLIGHT;

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const hasStartedConversation = messages.some((msg) => msg.sender === "user");
  const placeholderText = hasStartedConversation
    ? t("assistant.continueConversation", "Continue conversation…")
    : t("assistant.placeholder", "Ask elelem Assistant…");
  const [inputValue, setInputValue] = useState("");
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const isBusyRef = useRef(false);
  const [isHydrating, setIsHydrating] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isRestarting, setIsRestarting] = useState(false);
  const isBusy = isBotTyping || isStreaming || isUploadingFile || isRestarting || isHydrating;
  const prevClientIdRef = useRef(currentClientId);
  const [isStudioOpen, setIsStudioOpen] = useState(() => {
    if (typeof window !== "undefined" && window.innerWidth >= 1200) {
      const mode =
        getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_MODE_KEY) ||
        C.ASSISTANT_MODE_SPOTLIGHT;
      return mode === C.ASSISTANT_MODE_SPOTLIGHT;
    }
    return false;
  });
  const [hasOpenedStudio, setHasOpenedStudio] = useState(isStudioOpen);
  const historyWasOpenBeforeStudioRef = useRef(
    typeof window !== "undefined" && window.innerWidth >= 1200
      ? (getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_MODE_KEY) ||
          C.ASSISTANT_MODE_SPOTLIGHT) === C.ASSISTANT_MODE_SPOTLIGHT
      : false
  );
  const [isHistoryOpen, setIsHistoryOpen] = useState(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth <= 768) {
        return false;
      }
      if (window.innerWidth >= 1200) {
        const mode =
          getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_MODE_KEY) ||
          C.ASSISTANT_MODE_SPOTLIGHT;
        if (mode === C.ASSISTANT_MODE_SPOTLIGHT) {
          return false;
        }
      }
    }
    return true;
  });
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [starterSuggestions, setStarterSuggestions] = useState([]);
  const [showSpotlightSuggestions, setShowSpotlightSuggestions] = useState(false);
  const [showSteps, setShowSteps] = useState(() => {
    const stored = getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_SHOW_STEPS_KEY);
    if (stored === null || stored === undefined) return true;
    return stored === "true" || stored === true;
  });
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);
  const overflowMenuRef = useRef(null);
  const isAtBottomRef = useRef(true);

  const toggleShowSteps = () => {
    setShowSteps((prev) => {
      const next = !prev;
      setLocalStorageWithExpiry(
        C.ELELEM_ASSISTANT_SHOW_STEPS_KEY,
        String(next),
        C.EXPIRY_TIME_IN_MINUTES,
      );
      window.dispatchEvent(
        new CustomEvent("elelem-assistant:show-steps-change", {
          detail: next,
        }),
      );
      return next;
    });
  };
  const [historyWidth, setHistoryWidth] = useState(() => {
    const stored = getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_HISTORY_WIDTH_KEY);
    const parsed = parseInt(stored, 10);
    return !isNaN(parsed) &&
      parsed >= C.MIN_ASSISTANT_HISTORY_WIDTH &&
      parsed <= C.MAX_ASSISTANT_HISTORY_WIDTH
      ? parsed
      : C.DEFAULT_ASSISTANT_HISTORY_WIDTH;
  });
  const [studioWidth, setStudioWidth] = useState(() => {
    const stored = getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_STUDIO_WIDTH_KEY);
    const parsed = parseInt(stored, 10);
    return !isNaN(parsed) &&
      parsed >= C.MIN_ASSISTANT_STUDIO_WIDTH &&
      parsed <= C.MAX_ASSISTANT_STUDIO_WIDTH
      ? parsed
      : C.DEFAULT_ASSISTANT_STUDIO_WIDTH;
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const stored = getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_SIDEBAR_WIDTH_KEY);
    const parsed = parseInt(stored, 10);
    return !isNaN(parsed) &&
      parsed >= C.MIN_ASSISTANT_SIDEBAR_WIDTH &&
      parsed <= C.MAX_ASSISTANT_SIDEBAR_WIDTH
      ? parsed
      : C.DEFAULT_ASSISTANT_SIDEBAR_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);
  const resizingRef = useRef(null);

  const notificationDropdownRef = useRef(null);
  const spotlightContainerRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fabRef = useRef(null);
  const fileInputRef = useRef(null);
  const focusTimerRef = useRef(null);
  const hasOpenedRef = useRef(false); // tracks whether the panel has been opened at least once
  const conversationIdRef = useRef(null);
  const preventFocusOpenRef = useRef(false);
  const justFocusedRef = useRef(false);
  const justFocusedTimerRef = useRef(null);
  const uploadTokenRef = useRef(0); // incremented on restart/new upload to invalidate stale async completions
  const requestIdRef = useRef(0); // incremented on restart/new message to invalidate stale in-flight responses
  const activeStreamTurnIdRef = useRef(null); // tracks active streaming botMsgId to isolate per-turn stream state
  const activeServerTurnIdRef = useRef(null); // tracks backend-issued turn_id for cancellation
  const isNewConversationRef = useRef(false); // tracks if current in-flight stream started a new conversation
  const cancelledTurnIdsRef = useRef(new Set()); // tracks explicitly cancelled turns (restart / delete / stop)
  const abortControllerRef = useRef(null); // active AbortController for in-flight fetch requests
  const suggestionTokenRef = useRef(0); // incremented on restart/language change to invalidate stale starter suggestions
  const starterSuggestionsRef = useRef([]);
  const activeSuggestionsFetchRef = useRef(null);
  const hydrationTokenRef = useRef(0); // incremented to cancel/invalidate pending conversation hydration
  const historyTokenRef = useRef(0); // incremented to invalidate stale conversation history fetches
  const deletedConvIdsRef = useRef(new Set()); // tracks deleted conversation IDs to prevent resurrection from stale history responses

  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  }, [t]);

  useEffect(() => {
    starterSuggestionsRef.current = starterSuggestions;
  }, [starterSuggestions]);

  const hasHandledInitialLanguageRef = useRef(false);

  const {
    notifications,
    unreadCount,
    isLoading: isLoadingNotifications,
    markAsSeen: markNotificationAsSeen,
    markAllAsRead,
    refreshNotifications,
    dismissAllToasts,
  } = useNotifications();

  const toggleNotifications = () => {
    const next = !showNotifications;
    setShowNotifications(next);
    if (next) {
      dismissAllToasts();
      refreshNotifications({ silent: true });
    }
  };

  useEffect(() => {
    const handleShowStepsChange = (e) => {
      if (typeof e.detail === "boolean") {
        setShowSteps(e.detail);
      }
    };
    window.addEventListener("elelem-assistant:show-steps-change", handleShowStepsChange);
    return () => {
      window.removeEventListener("elelem-assistant:show-steps-change", handleShowStepsChange);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(e.target)
      ) {
        setShowNotifications(false);
      }
      if (
        overflowMenuRef.current &&
        !overflowMenuRef.current.contains(e.target)
      ) {
        setShowOverflowMenu(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        if (showNotifications) setShowNotifications(false);
        if (showOverflowMenu) setShowOverflowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showNotifications, showOverflowMenu]);

  const handleSpotlightInputClick = (isClick = false) => {
    if (!isOpen) {
      if (hasStartedConversation) {
        setIsOpen(true);
      } else if (showSpotlightSuggestions) {
        if (isClick && !justFocusedRef.current) {
          setIsOpen(true);
          setShowSpotlightSuggestions(false);
        }
      } else {
        setShowSpotlightSuggestions(true);
        if (starterSuggestions.length === 0) {
          const currentToken = suggestionTokenRef.current;
          const activeLang = i18n.resolvedLanguage || i18n.language || "en";
          const langCode = activeLang.startsWith("ja") ? "ja" : "en";
          fetchSuggestions(langCode).then((sugs) => {
            if (currentToken === suggestionTokenRef.current && sugs && sugs.length > 0) {
              setStarterSuggestions(sugs);
              starterSuggestionsRef.current = sugs;
              setMessages((prev) =>
                prev.length === 1 && prev[0].id === "welcome"
                  ? [{ ...prev[0], suggestions: sugs }]
                  : prev,
              );
            }
          });
        }
      }
    }
  };

  useEffect(() => {
    const handleSpotlightClickOutside = (e) => {
      if (
        spotlightContainerRef.current &&
        !spotlightContainerRef.current.contains(e.target)
      ) {
        setShowSpotlightSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleSpotlightClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleSpotlightClickOutside);
      if (justFocusedTimerRef.current) {
        clearTimeout(justFocusedTimerRef.current);
        justFocusedTimerRef.current = null;
      }
    };
  }, []);

  const handleOpenStudio = useCallback(() => {
    setHasOpenedStudio(true);
    setIsStudioOpen(true);
    setIsHistoryOpen((prev) => {
      if (prev) {
        historyWasOpenBeforeStudioRef.current = true;
        return false;
      }
      return prev;
    });
  }, []);

  const handleCloseStudio = useCallback(() => {
    setIsStudioOpen(false);
    if (historyWasOpenBeforeStudioRef.current) {
      setIsHistoryOpen(true);
      historyWasOpenBeforeStudioRef.current = false;
    }
  }, []);

  const handleExpandStudioToHalfScreen = useCallback(() => {
    if (typeof window === "undefined") return;
    setHasOpenedStudio(true);
    setIsStudioOpen(true);
    const halfWidth = Math.round(window.innerWidth * 0.5);
    const clamped = Math.max(
      C.MIN_ASSISTANT_STUDIO_WIDTH,
      Math.min(halfWidth, C.MAX_ASSISTANT_STUDIO_WIDTH),
    );
    setStudioWidth(clamped);
    setLocalStorageWithExpiry(
      C.ELELEM_ASSISTANT_STUDIO_WIDTH_KEY,
      clamped,
      C.EXPIRY_TIME_IN_MINUTES,
    );
    setIsHistoryOpen((prev) => {
      if (prev) {
        historyWasOpenBeforeStudioRef.current = true;
        return false;
      }
      return prev;
    });
  }, []);

  const toggleStudio = useCallback(() => {
    if (!isStudioOpen) {
      handleOpenStudio();
    } else {
      handleCloseStudio();
    }
  }, [isStudioOpen, handleOpenStudio, handleCloseStudio]);

  const toggleAssistantMode = () => {
    const nextMode = isSpotlightMode
      ? C.ASSISTANT_MODE_BOTTOM_RIGHT
      : C.ASSISTANT_MODE_SPOTLIGHT;
    setAssistantMode(nextMode);
    if (
      nextMode === C.ASSISTANT_MODE_SPOTLIGHT &&
      typeof window !== "undefined" &&
      window.innerWidth >= 1200
    ) {
      handleOpenStudio();
    } else {
      handleCloseStudio();
    }
    setLocalStorageWithExpiry(
      C.ELELEM_ASSISTANT_MODE_KEY,
      nextMode,
      C.EXPIRY_TIME_IN_MINUTES,
    );
    window.dispatchEvent(
      new CustomEvent("elelem-assistant:mode-change", { detail: nextMode }),
    );
  };

  const handleHistoryResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = {
      type: "history",
      startX: e.clientX,
      startWidth: historyWidth,
    };
    setIsResizing(true);
  };

  const handleStudioResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = {
      type: "studio",
      startX: e.clientX,
      startWidth: studioWidth,
    };
    setIsResizing(true);
  };

  const handleSidebarResizeStart = (e) => {
    e.preventDefault();
    resizingRef.current = {
      type: "sidebar",
      startX: e.clientX,
      startWidth: sidebarWidth,
    };
    setIsResizing(true);
  };

  const handleHistoryResetWidth = () => {
    setHistoryWidth(C.DEFAULT_ASSISTANT_HISTORY_WIDTH);
    setLocalStorageWithExpiry(
      C.ELELEM_ASSISTANT_HISTORY_WIDTH_KEY,
      C.DEFAULT_ASSISTANT_HISTORY_WIDTH,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const handleStudioResetWidth = () => {
    setStudioWidth(C.DEFAULT_ASSISTANT_STUDIO_WIDTH);
    setLocalStorageWithExpiry(
      C.ELELEM_ASSISTANT_STUDIO_WIDTH_KEY,
      C.DEFAULT_ASSISTANT_STUDIO_WIDTH,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const handleSidebarResetWidth = () => {
    setSidebarWidth(C.DEFAULT_ASSISTANT_SIDEBAR_WIDTH);
    setLocalStorageWithExpiry(
      C.ELELEM_ASSISTANT_SIDEBAR_WIDTH_KEY,
      C.DEFAULT_ASSISTANT_SIDEBAR_WIDTH,
      C.EXPIRY_TIME_IN_MINUTES,
    );
  };

  const handleHistoryResizerKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setHistoryWidth((w) => {
        const next = Math.max(C.MIN_ASSISTANT_HISTORY_WIDTH, w - 10);
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_HISTORY_WIDTH_KEY,
          next,
          C.EXPIRY_TIME_IN_MINUTES,
        );
        return next;
      });
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setHistoryWidth((w) => {
        const next = Math.min(C.MAX_ASSISTANT_HISTORY_WIDTH, w + 10);
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_HISTORY_WIDTH_KEY,
          next,
          C.EXPIRY_TIME_IN_MINUTES,
        );
        return next;
      });
    }
  };

  const handleStudioResizerKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setStudioWidth((w) => {
        const next = Math.min(C.MAX_ASSISTANT_STUDIO_WIDTH, w + 10);
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_STUDIO_WIDTH_KEY,
          next,
          C.EXPIRY_TIME_IN_MINUTES,
        );
        return next;
      });
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setStudioWidth((w) => {
        const next = Math.max(C.MIN_ASSISTANT_STUDIO_WIDTH, w - 10);
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_STUDIO_WIDTH_KEY,
          next,
          C.EXPIRY_TIME_IN_MINUTES,
        );
        return next;
      });
    }
  };

  const handleSidebarResizerKeyDown = (e) => {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      setSidebarWidth((w) => {
        const next = Math.min(C.MAX_ASSISTANT_SIDEBAR_WIDTH, w + 10);
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_SIDEBAR_WIDTH_KEY,
          next,
          C.EXPIRY_TIME_IN_MINUTES,
        );
        return next;
      });
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      setSidebarWidth((w) => {
        const next = Math.max(C.MIN_ASSISTANT_SIDEBAR_WIDTH, w - 10);
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_SIDEBAR_WIDTH_KEY,
          next,
          C.EXPIRY_TIME_IN_MINUTES,
        );
        return next;
      });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!resizingRef.current) return;
      const { type, startX, startWidth } = resizingRef.current;
      const deltaX = e.clientX - startX;

      if (type === "history") {
        const maxWidth = Math.min(
          C.MAX_ASSISTANT_HISTORY_WIDTH,
          Math.floor(window.innerWidth * 0.45),
        );
        const next = Math.max(
          C.MIN_ASSISTANT_HISTORY_WIDTH,
          Math.min(startWidth + deltaX, maxWidth),
        );
        setHistoryWidth(next);
      } else if (type === "studio") {
        const maxWidth = Math.min(
          C.MAX_ASSISTANT_STUDIO_WIDTH,
          Math.floor(window.innerWidth * 0.75),
        );
        const next = Math.max(
          C.MIN_ASSISTANT_STUDIO_WIDTH,
          Math.min(startWidth - deltaX, maxWidth),
        );
        setStudioWidth(next);
      } else if (type === "sidebar") {
        const maxWidth = Math.min(
          C.MAX_ASSISTANT_SIDEBAR_WIDTH,
          Math.floor(window.innerWidth * 0.85),
        );
        const next = Math.max(
          C.MIN_ASSISTANT_SIDEBAR_WIDTH,
          Math.min(startWidth - deltaX, maxWidth),
        );
        setSidebarWidth(next);
      }
    };

    const handleMouseUp = () => {
      if (!resizingRef.current) return;
      const { type } = resizingRef.current;
      resizingRef.current = null;
      setIsResizing(false);

      if (type === "history") {
        setHistoryWidth((current) => {
          setLocalStorageWithExpiry(
            C.ELELEM_ASSISTANT_HISTORY_WIDTH_KEY,
            current,
            C.EXPIRY_TIME_IN_MINUTES,
          );
          return current;
        });
      } else if (type === "studio") {
        setStudioWidth((current) => {
          setLocalStorageWithExpiry(
            C.ELELEM_ASSISTANT_STUDIO_WIDTH_KEY,
            current,
            C.EXPIRY_TIME_IN_MINUTES,
          );
          return current;
        });
      } else if (type === "sidebar") {
        setSidebarWidth((current) => {
          setLocalStorageWithExpiry(
            C.ELELEM_ASSISTANT_SIDEBAR_WIDTH_KEY,
            current,
            C.EXPIRY_TIME_IN_MINUTES,
          );
          return current;
        });
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const formatHistoryMessages = useCallback((rawMessages) => {
    return (rawMessages || []).map((m, idx) => {
      const isHuman = m.type === "human" || m.type === "user";
      const sender = isHuman ? "user" : "bot";
      let rawContent = m.data?.content || "";
      let text = "";
      let attachments = [];

      if (Array.isArray(rawContent)) {
        const textPart = rawContent.find((b) => b.type === "text");
        text = textPart?.text || "";
        attachments = rawContent
          .filter((b) => b.type === "media")
          .map((b) => ({
            file_name: b.file_name || "Attachment",
            mime_type: b.mime_type,
            gcs_uri: b.file_uri,
          }));
      } else {
        text = String(rawContent);
      }

      return {
        id: `history-${idx}`,
        sender,
        text,
        attachments,
        animate: false,
        stopped: Boolean(m.data?.stopped || m.stopped),
        sources: m.data?.sources || [],
        suggestions: [],
        reasoning_steps: m.data?.reasoning_steps || m.reasoning_steps || [],
        thoughts: m.data?.thoughts || m.thoughts || [],
        turn_elapsed_ms: m.data?.turn_elapsed_ms || m.turn_elapsed_ms || null,
      };
    });
  }, []);

  const scrollToBottom = useCallback((behavior = "auto") => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      if (typeof container.scrollTo === "function") {
        container.scrollTo({ top: container.scrollHeight, behavior });
      } else {
        container.scrollTop = container.scrollHeight;
      }
    } else if (typeof messagesEndRef.current?.scrollIntoView === "function") {
      messagesEndRef.current.scrollIntoView({ behavior });
    }
  }, []);

  const hydrateConversation = useCallback(
    async (convId) => {
      if (!convId) return false;
      hydrationTokenRef.current += 1;
      const myToken = hydrationTokenRef.current;
      try {
        const res = await fetch(
          `${BACKEND_URL}/ai-assistant/conversations/${convId}`,
          {
            credentials: "include",
          },
        );
        if (!res.ok) return false;

        const data = await res.json();
        if (myToken !== hydrationTokenRef.current) return false;
        if (deletedConvIdsRef.current.has(convId)) return false;

        if (data?.conversation_id) {
          conversationIdRef.current = data.conversation_id;
        }

        const formatted = formatHistoryMessages(data.messages);
        if (formatted.length > 0) {
          setMessages((prev) => {
            // Reconcile: preserve any user turns submitted while hydration was in-flight
            const pendingUserTurns = prev.filter(
              (m) => m.sender === "user" && !m.id.startsWith("history-"),
            );
            if (pendingUserTurns.length > 0) {
              return [...formatted, ...pendingUserTurns];
            }
            return formatted;
          });
          isAtBottomRef.current = true;
          setTimeout(() => {
            scrollToBottom("auto");
          }, 50);
          return true;
        }
      } catch (err) {
        console.error("Failed to hydrate conversation:", err);
      }
      return false;
    },
    [BACKEND_URL, formatHistoryMessages, scrollToBottom],
  );

  // Fetch Conversation History
  const fetchConversations = useCallback(async () => {
    historyTokenRef.current += 1;
    const currentToken = historyTokenRef.current;
    setConversationsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/ai-assistant/conversations`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to fetch conversation history");
      const data = await res.json();
      if (currentToken !== historyTokenRef.current) return;
      // Only display conversations that have actual messages (filter out empty draft sessions)
      // and exclude any deleted conversations to prevent stale resurrection
      const validConversations = (Array.isArray(data) ? data : []).filter(
        (c) =>
          !deletedConvIdsRef.current.has(c.conversation_id) &&
          (c.message_count === undefined || c.message_count > 0),
      );
      setConversations(validConversations);
    } catch (e) {
      if (currentToken !== historyTokenRef.current) return;
      console.error("Failed to fetch conversation history:", e);
      showApiError(
        tRef.current("assistant.loadHistoryError", "Failed to load historical conversation"),
      );
    } finally {
      if (currentToken === historyTokenRef.current) {
        setConversationsLoading(false);
      }
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    if (isSpotlightMode && isOpen) {
      fetchConversations();
    }
  }, [isSpotlightMode, isOpen, fetchConversations]);

  const selectConversation = async (convId) => {
    if (convId === conversationIdRef.current || isBusy || isBusyRef.current) return;
    setIsHydrating(true);
    hydrationTokenRef.current += 1;
    const myToken = hydrationTokenRef.current;

    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      setIsHistoryOpen(false);
    }

    try {
      const res = await fetch(`${BACKEND_URL}/ai-assistant/conversations/${convId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load conversation");

      const data = await res.json();
      if (myToken !== hydrationTokenRef.current) return;
      if (deletedConvIdsRef.current.has(convId)) return;

      if (data?.conversation_id) {
        conversationIdRef.current = data.conversation_id;
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY,
          data.conversation_id,
          C.EXPIRY_TIME_IN_MINUTES,
        );
      }

      const formatted = formatHistoryMessages(data.messages);
      if (formatted.length > 0) {
        setMessages(formatted);
        isAtBottomRef.current = true;
        setTimeout(() => {
          scrollToBottom("auto");
        }, 50);
      } else {
        await initSession();
      }
    } catch (err) {
      if (myToken !== hydrationTokenRef.current) return;
      console.error("Failed to select conversation:", err);
      showApiError(
        tRef.current("assistant.loadHistoryError", "Failed to load historical conversation"),
      );
    } finally {
      if (myToken === hydrationTokenRef.current) {
        setIsHydrating(false);
      }
    }
  };

  const handleStartRename = (e, conv) => {
    e.stopPropagation();
    setEditingConversationId(conv.conversation_id);
    setEditingTitle(conv.title);
  };

  const handleSaveRename = async (e, convId) => {
    e.stopPropagation();
    if (!editingTitle.trim()) {
      setEditingConversationId(null);
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/ai-assistant/conversations/${convId}`, {
        credentials: "include",
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: editingTitle.trim() }),
      });
      if (!res.ok) throw new Error("Failed to rename conversation");

      setConversations((prev) =>
        prev.map((c) =>
          c.conversation_id === convId ? { ...c, title: editingTitle.trim() } : c,
        ),
      );
      showSuccess(
        tRef.current("assistant.conversationTitleUpdated", "Conversation title updated"),
      );
    } catch (err) {
      console.error("Failed to rename conversation:", err);
      showApiError(
        tRef.current("assistant.renameHistoryError", "Failed to rename conversation"),
      );
    } finally {
      setEditingConversationId(null);
    }
  };

  const handleDeleteConversation = async (e, convId) => {
    e.stopPropagation();
    const confirmed = window.confirm(
      tRef.current(
        "assistant.confirmDeleteConversation",
        "Are you sure you want to delete this conversation?",
      ),
    );
    if (!confirmed) return;

    try {
      const res = await fetch(`${BACKEND_URL}/ai-assistant/conversations/${convId}`, {
        credentials: "include",
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete conversation");

      deletedConvIdsRef.current.add(convId);
      historyTokenRef.current += 1;
      setConversations((prev) => prev.filter((c) => c.conversation_id !== convId));

      const activeOrPersistedId =
        conversationIdRef.current ||
        getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY);

      if (activeOrPersistedId === convId) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (activeStreamTurnIdRef.current) {
          cancelledTurnIdsRef.current.add(activeStreamTurnIdRef.current);
          activeStreamTurnIdRef.current = null;
        }
        hydrationTokenRef.current += 1;
        uploadTokenRef.current += 1;
        requestIdRef.current += 1;
        suggestionTokenRef.current += 1;
        conversationIdRef.current = null;
        setLocalStorageWithExpiry(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY, null);
        setMessages([]);
        setInputValue("");
        setAttachedFiles([]);
        setUploadError(null);
        setIsUploadingFile(false);
        setIsBotTyping(false);
        setIsStreaming(false);
        isBusyRef.current = false;
        setIsHydrating(false);
        await initSession();
      }
    } catch (err) {
      console.error("Failed to delete conversation:", err);
      showApiError(
        tRef.current("assistant.deleteHistoryError", "Failed to delete conversation"),
      );
    }
  };

  const historyGroups = groupConversationsByDate(conversations, t);

  // Fetch AI-generated starter/follow-up suggestion prompts for the given language
  const fetchSuggestions = useCallback(
    async (langCode) => {
      if (
        activeSuggestionsFetchRef.current &&
        activeSuggestionsFetchRef.current.langCode === langCode
      ) {
        return activeSuggestionsFetchRef.current.promise;
      }

      const promise = (async () => {
        try {
          const res = await fetch(
            `${BACKEND_URL}/ai-assistant/suggestions?language=${langCode}`,
            {
              credentials: "include",
              headers: { "Accept-Language": langCode },
            },
          );
          if (!res.ok) return [];
          const data = await res.json();
          return Array.isArray(data?.suggestions) ? data.suggestions : [];
        } catch (err) {
          console.error("Failed to fetch elelem Assistant suggestions:", err);
          return [];
        } finally {
          if (
            activeSuggestionsFetchRef.current &&
            activeSuggestionsFetchRef.current.promise === promise
          ) {
            activeSuggestionsFetchRef.current = null;
          }
        }
      })();

      activeSuggestionsFetchRef.current = { langCode, promise };
      return promise;
    },
    [BACKEND_URL],
  );

  // Auto-resize the textarea height based on its content
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [inputValue]);

  // Initialize session
  const initSession = useCallback(async (silent = false, initialConvId = null) => {
    if (!silent) {
      setIsBotTyping(true);
    }
    suggestionTokenRef.current += 1;
    const currentToken = suggestionTokenRef.current;

    const storedConvId =
      initialConvId ||
      conversationIdRef.current ||
      getLocalStorageWithExpiry(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY);

    if (storedConvId && !initialConvId) {
      setIsHydrating(true);
      try {
        const hydrated = await hydrateConversation(storedConvId);
        if (hydrated) {
          setIsBotTyping(false);
          if (isSpotlightMode) {
            fetchConversations();
          }
          return;
        }
        conversationIdRef.current = null;
        setLocalStorageWithExpiry(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY, null);
      } finally {
        setIsHydrating(false);
      }
    }

    try {
      const firstname = getLocalStorageWithExpiry("firstname") || "Guest";
      const client_id = getLocalStorageWithExpiry("client_id");
      const projects = getLocalStorageWithExpiry("projects") || [];
      const currentProject = projects.find((p) => p.id === client_id);
      const brandName = currentProject?.name || projects?.[0]?.name || "Elelem";

      const welcomeMsg = tRef.current("assistant.welcome", {
        firstname,
        brandName,
        defaultValue: `Hello **${firstname}**! I am your elelem Assistant for **${brandName}**. How can I help you today?`,
      });
      const initialSuggestions =
        starterSuggestionsRef.current && starterSuggestionsRef.current.length > 0
          ? starterSuggestionsRef.current
          : getDefaultStarterSuggestions(tRef.current);
      setMessages([
        {
          id: "welcome",
          sender: "bot",
          text: welcomeMsg,
          animate: false,
          suggestions: initialSuggestions,
        },
      ]);

      const activeLang = i18n.resolvedLanguage || i18n.language || "en";
      const langCode = activeLang.startsWith("ja") ? "ja" : "en";
      const suggestions = await fetchSuggestions(langCode);
      if (currentToken === suggestionTokenRef.current) {
        const hasSuggestions =
          Array.isArray(suggestions) && suggestions.length > 0;
        setStarterSuggestions(hasSuggestions ? suggestions : []);
        starterSuggestionsRef.current = hasSuggestions ? suggestions : [];
        setMessages((prev) =>
          prev.length === 1 && prev[0].id === "welcome"
            ? [
                {
                  ...prev[0],
                  suggestions: hasSuggestions
                    ? suggestions
                    : getDefaultStarterSuggestions(tRef.current),
                },
              ]
            : prev,
        );
      }
    } catch (err) {
      console.error("Failed to initialize elelem Assistant session:", err);
      setMessages([
        {
          id: "error",
          sender: "bot",
          text: tRef.current(
            "assistant.initError",
            "Failed to initialize. Please check connection and try again.",
          ),
          animate: false,
        },
      ]);
    } finally {
      setIsBotTyping(false);
    }
    // i18n.language is read fresh via closure on every call (mount + restart); language
    // changes to an already-open welcome screen are handled by the effect below instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchSuggestions, hydrateConversation]);

  useEffect(() => {
    initSession(true);
  }, [initSession]);

  // Handle project switching: reset conversation state and hydrate fresh session for new client
  useEffect(() => {
    if (
      prevClientIdRef.current &&
      currentClientId &&
      prevClientIdRef.current !== currentClientId
    ) {
      prevClientIdRef.current = currentClientId;
      conversationIdRef.current = null;
      try {
        localStorage.removeItem(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY);
      } catch (e) {
        // ignore
      }
      setMessages([]);
      initSession(true);
    } else if (currentClientId) {
      prevClientIdRef.current = currentClientId;
    }
  }, [currentClientId, initSession]);

  // Update welcome message and re-fetch starter suggestions if language changes before a conversation begins
  useEffect(() => {
    if (!hasHandledInitialLanguageRef.current) {
      hasHandledInitialLanguageRef.current = true;
      return;
    }

    setMessages((prev) => {
      if (prev.length === 1 && prev[0].id === "welcome") {
        const firstname = getLocalStorageWithExpiry("firstname") || "Guest";
        const client_id = getLocalStorageWithExpiry("client_id");
        const projects = getLocalStorageWithExpiry("projects") || [];
        const currentProject = projects.find((p) => p.id === client_id);
        const brandName = currentProject?.name || projects?.[0]?.name || "Elelem";
        const welcomeMsg = tRef.current("assistant.welcome", {
          firstname,
          brandName,
          defaultValue: `Hello **${firstname}**! I am your elelem Assistant for **${brandName}**. How can I help you today?`,
        });
        return [{ ...prev[0], text: welcomeMsg }];
      }
      return prev;
    });

    suggestionTokenRef.current += 1;
    const currentToken = suggestionTokenRef.current;
    activeSuggestionsFetchRef.current = null;
    const activeLang = i18n.resolvedLanguage || i18n.language || "en";
    const langCode = activeLang.startsWith("ja") ? "ja" : "en";
    fetchSuggestions(langCode).then((suggestions) => {
      if (currentToken === suggestionTokenRef.current) {
        const hasSuggestions =
          Array.isArray(suggestions) && suggestions.length > 0;
        setStarterSuggestions(hasSuggestions ? suggestions : []);
        starterSuggestionsRef.current = hasSuggestions ? suggestions : [];
        setMessages((prev) =>
          prev.length === 1 && prev[0].id === "welcome"
            ? [
                {
                  ...prev[0],
                  suggestions: hasSuggestions
                    ? suggestions
                    : getDefaultStarterSuggestions(tRef.current),
                },
              ]
            : prev,
        );
      }
    });
  }, [i18n.language, i18n.resolvedLanguage, fetchSuggestions]);

  // Auto-fetch suggestions when opening full-screen/sidebar view if not already loaded and no conversation has started
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (
      !wasOpen &&
      isOpen &&
      !hasStartedConversation &&
      starterSuggestions.length === 0
    ) {
      const currentToken = suggestionTokenRef.current;
      const activeLang = i18n.resolvedLanguage || i18n.language || "en";
      const langCode = activeLang.startsWith("ja") ? "ja" : "en";
      fetchSuggestions(langCode).then((sugs) => {
        if (
          currentToken === suggestionTokenRef.current &&
          sugs &&
          sugs.length > 0
        ) {
          setStarterSuggestions(sugs);
          starterSuggestionsRef.current = sugs;
          setMessages((prev) =>
            prev.length === 1 && prev[0].id === "welcome"
              ? [{ ...prev[0], suggestions: sugs }]
              : prev,
          );
        }
      });
    }
  }, [
    isOpen,
    hasStartedConversation,
    starterSuggestions.length,
    fetchSuggestions,
    i18n.language,
    i18n.resolvedLanguage,
  ]);

  useEffect(() => {
    const handleModeChange = (e) => {
      if (e.detail) {
        setAssistantMode(e.detail);
        if (
          e.detail === C.ASSISTANT_MODE_SPOTLIGHT &&
          typeof window !== "undefined" &&
          window.innerWidth >= 1200
        ) {
          handleOpenStudio();
        } else {
          handleCloseStudio();
        }
      }
    };
    window.addEventListener("elelem-assistant:mode-change", handleModeChange);
    return () => {
      window.removeEventListener("elelem-assistant:mode-change", handleModeChange);
    };
  }, [handleOpenStudio, handleCloseStudio]);

  // Listen for studio width expansion events (e.g. Ground Truth 50% screen width capture)
  useEffect(() => {
    const handleExpandStudioEvent = () => {
      handleExpandStudioToHalfScreen();
    };
    window.addEventListener("elelem-assistant:expand-studio-width", handleExpandStudioEvent);
    return () => {
      window.removeEventListener("elelem-assistant:expand-studio-width", handleExpandStudioEvent);
    };
  }, [handleExpandStudioToHalfScreen]);

  // Listen for external elelem Assistant prompts (and legacy copilot prompts)
  useEffect(() => {
    const handleSendPrompt = (e) => {
      const promptText = e.detail;
      if (promptText) {
        setIsOpen(true);
        sendMessage(promptText);
      }
    };

    window.addEventListener("elelem-assistant:send-prompt", handleSendPrompt);
    window.addEventListener("copilot:send-prompt", handleSendPrompt);
    return () => {
      window.removeEventListener(
        "elelem-assistant:send-prompt",
        handleSendPrompt,
      );
      window.removeEventListener("copilot:send-prompt", handleSendPrompt);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBotTyping]);

  useEffect(() => {
    if (focusTimerRef.current) {
      clearTimeout(focusTimerRef.current);
      focusTimerRef.current = null;
    }

    if (isOpen) {
      hasOpenedRef.current = true;
      // Delay lets the drawer render before moving focus into the input
      focusTimerRef.current = setTimeout(() => {
        textareaRef.current?.focus();
      }, 50);
    } else if (hasOpenedRef.current) {
      // Return focus to the launcher only after a real close, not on initial mount
      if (isSpotlightMode) {
        preventFocusOpenRef.current = true;
        textareaRef.current?.focus();
        focusTimerRef.current = setTimeout(() => {
          preventFocusOpenRef.current = false;
        }, 100);
      } else {
        fabRef.current?.focus();
      }
    }

    return () => {
      if (focusTimerRef.current) {
        clearTimeout(focusTimerRef.current);
        focusTimerRef.current = null;
      }
    };
  }, [isOpen, isSpotlightMode]);

  const handleMessagesScroll = (e) => {
    const el = e.currentTarget;
    if (el) {
      // User is considered at bottom if within 120px of bottom
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      isAtBottomRef.current = distanceFromBottom < 120;
    }
  };

  // Scroll to bottom when messages list changes if user was already at bottom
  useEffect(() => {
    if (isAtBottomRef.current) {
      scrollToBottom(isStreaming ? "auto" : "smooth");
    }
  }, [messages, isStreaming, isBotTyping, scrollToBottom]);

  const handleRestart = async () => {
    if (isRestarting) return;

    if (typeof window !== "undefined" && window.innerWidth <= 768) {
      setIsHistoryOpen(false);
    }

    if (activeStreamTurnIdRef.current) {
      cancelledTurnIdsRef.current.add(activeStreamTurnIdRef.current);
      activeStreamTurnIdRef.current = null;
    }
    uploadTokenRef.current += 1;
    requestIdRef.current += 1;
    suggestionTokenRef.current += 1;
    activeSuggestionsFetchRef.current = null;
    conversationIdRef.current = null;
    setLocalStorageWithExpiry(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY, null);
    setIsRestarting(true);
    setIsStreaming(false);
    isBusyRef.current = false;

    try {
      const res = await fetch(`${BACKEND_URL}/ai-assistant/refresh`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        showApiError(
          tRef.current(
            "assistant.refreshError",
            "Failed to refresh conversation session. Please try again.",
          ),
        );
        setIsRestarting(false);
        return;
      }

      const data = typeof res.json === "function" ? await res.json().catch(() => null) : null;
      const newConvId = data?.conversation_id || null;
      conversationIdRef.current = newConvId;
      if (newConvId) {
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY,
          newConvId,
          C.EXPIRY_TIME_IN_MINUTES,
        );
      } else {
        try {
          localStorage.removeItem(C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY);
        } catch (e) {
          // ignore
        }
      }

      setMessages([]);
      setInputValue("");
      setAttachedFiles([]);
      setUploadError(null);
      setIsUploadingFile(false);
      setIsBotTyping(false);
      await initSession(false, newConvId);
      if (isSpotlightMode) {
        fetchConversations();
      }
    } catch (err) {
      console.error("Failed to refresh session on backend:", err);
      showApiError(
        tRef.current(
          "assistant.refreshError",
          "Failed to refresh conversation session. Please try again.",
        ),
      );
    } finally {
      setIsRestarting(false);
    }
  };

  const uploadFiles = async (files) => {
    if (!files || !files.length) return;

    uploadTokenRef.current += 1;
    const myToken = uploadTokenRef.current;

    setIsUploadingFile(true);
    setUploadError(null);

    try {
      for (let i = 0; i < files.length; i++) {
        let file = files[i];
        if (!file.name || file.name === "image.png" || file.name === "blob") {
          const ext = file.type ? file.type.split("/")[1] || "png" : "png";
          const newName = `pasted_image_${Date.now()}_${i + 1}.${ext}`;
          file = new File([file], newName, { type: file.type || "image/png" });
        }

        const formData = new FormData();
        formData.append("file", file);

        const res = await fetch(`${BACKEND_URL}/ai-assistant/upload-attachment`, {
          credentials: "include",
          method: "POST",
          body: formData,
        });

        if (uploadTokenRef.current !== myToken) return;

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          const hasPermissionMsg = typeof errData.detail === "string" && errData.detail.toLowerCase().includes("permission");
          if (hasPermissionMsg) {
            const errorMsg = tRef.current(
              "assistant.subscriptionUpgradeRequired",
              "Subscription Upgrade Required: You do not have permission to access this AI Assistant feature under your current plan. To upgrade your subscription tier, please contact support at tech-support@elelem.ai.",
            );
            showApiError(errorMsg);
            const err = new Error(errorMsg);
            err.isPermissionError = true;
            throw err;
          }
          throw new Error(errData.detail || `Upload failed for ${file.name}`);
        }

        const data = await res.json();
        if (uploadTokenRef.current !== myToken) return;
        setAttachedFiles((prev) => [
          ...prev,
          {
            file_name: data.file_name,
            mime_type: data.mime_type,
            gcs_uri: data.gcs_uri,
            file_id: data.file_id,
            download_url: data.download_url,
          },
        ]);
      }
    } catch (err) {
      if (uploadTokenRef.current !== myToken) return;
      console.error("Attachment upload error:", err);
      setUploadError(
        err.message ||
          t("assistant.uploadError", "Failed to upload file"),
      );
    } finally {
      if (uploadTokenRef.current === myToken) {
        setIsUploadingFile(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    uploadFiles(files);
  };

  const handlePaste = (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) return;

    const items = Array.from(clipboardData.items || []);
    const pastedFiles = [];

    for (const item of items) {
      if (item.kind === "file" && item.type.startsWith("image/")) {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }

    if (pastedFiles.length > 0) {
      e.preventDefault();
      uploadFiles(pastedFiles);
    }
  };

  const removeAttachment = (index) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async (text) => {
    const hasText = Boolean(text && text.trim());
    const hasAttachments = attachedFiles.length > 0;
    if ((!hasText && !hasAttachments) || isBusy || isBusyRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const abortSignal = abortControllerRef.current.signal;

    isBusyRef.current = true;
    setIsStreaming(true);
    setIsBotTyping(true);
    isNewConversationRef.current = !conversationIdRef.current;

    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;

    if (!isOpen) {
      setIsOpen(true);
    }

    const requestStartedAt = Date.now();
    const userMsgId = `user-${Date.now()}`;
    const botMsgId = `bot-${Date.now()}`;
    const currentAttachments = [...attachedFiles];

    // Append user message and stop any active previous typewriter animations
    setMessages((prev) => {
      const existing = prev.map((msg) =>
        msg.sender === "bot" && msg.animate ? { ...msg, animate: false } : msg,
      );
      return [
        ...existing,
        { id: userMsgId, sender: "user", text, attachments: currentAttachments },
      ];
    });

    setInputValue("");
    setAttachedFiles([]);
    setUploadError(null);
    setIsBotTyping(true);

    const activeLang = i18n.resolvedLanguage || i18n.language || "en";
    const langCode = activeLang.startsWith("ja") ? "ja" : "en";
    const defaultAttachmentPrompt =
      langCode === "ja"
        ? "添付ファイルを分析してください"
        : "Analyze attached file(s)";

    const isDebugRest = () => {
      try {
        if (typeof window !== "undefined") {
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get("transport") === "rest") return true;
          const stored = localStorage.getItem("elelem_assistant_transport");
          if (stored === "rest") return true;
        }
      } catch (e) {
        // ignore
      }
      return false;
    };

    const handleRestPayload = (data) => {
      if (currentRequestId !== requestIdRef.current) return;
      if (data?.conversation_id) {
        const isNewConversation = conversationIdRef.current !== data.conversation_id;
        conversationIdRef.current = data.conversation_id;
        setLocalStorageWithExpiry(
          C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY,
          data.conversation_id,
          C.EXPIRY_TIME_IN_MINUTES,
        );
        if (isSpotlightMode && isNewConversation) {
          fetchConversations();
        }
      }
      isBusyRef.current = false;
      setIsStreaming(false);
      setIsBotTyping(false);
      setMessages((prev) => {
        const botTurn = {
          id: botMsgId,
          sender: "bot",
          text: data.response,
          animate: true,
          sources: data.sources || [],
          suggestions: Array.isArray(data.suggestions) ? data.suggestions : [],
          reasoning_steps: Array.isArray(data.reasoning_steps) ? data.reasoning_steps : [],
          thoughts: Array.isArray(data.thoughts) ? data.thoughts : [],
          turn_elapsed_ms: typeof data.turn_elapsed_ms === "number" ? data.turn_elapsed_ms : null,
        };
        const existingIdx = prev.findIndex((m) => m.id === botMsgId);
        if (existingIdx !== -1) {
          const updated = [...prev];
          updated[existingIdx] = botTurn;
          return updated;
        }
        return [...prev, botTurn];
      });
      triggerAiUsageRefresh();
    };

    const executeRestFallback = async () => {
      const restRes = await fetch(`${BACKEND_URL}/ai-assistant/response`, {
        credentials: "include",
        method: "POST",
        signal: abortSignal,
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": langCode,
        },
        body: JSON.stringify({
          user_prompt: hasText ? text : defaultAttachmentPrompt,
          attachments: currentAttachments.length > 0 ? currentAttachments : undefined,
          conversation_id: conversationIdRef.current || undefined,
        }),
      });

      if (!restRes.ok) {
        const errData = await restRes.json().catch(() => ({}));
        const detailObj = typeof errData.detail === "object" && errData.detail !== null ? errData.detail : {};
        const errCode = detailObj.code || errData.code;
        const detailStr = detailObj.message || (typeof errData.detail === "string" ? errData.detail : "");

        const isQuotaExceeded = errCode === "quota_exceeded" || detailStr.toLowerCase().includes("quota") || detailStr.includes("クォータ");
        if (isQuotaExceeded) {
          const quotaMsg = tRef.current(
            "assistant.quotaExceeded",
            "AI Quota Exceeded: Your organization has reached its monthly AI usage quota. Please contact your administrator to upgrade your plan.",
          );
          showApiError(quotaMsg);
          const err = new Error(quotaMsg);
          err.isQuotaError = true;
          throw err;
        }

        if (errCode === "response_in_progress" || (restRes.status === 429 && !isQuotaExceeded)) {
          const progressMsg = detailStr || tRef.current(
            "assistant.responseInProgress",
            "A response is currently being generated. Please wait a moment.",
          );
          showApiError(progressMsg);
          const err = new Error(progressMsg);
          err.isConcurrencyError = true;
          throw err;
        }

        const hasPermissionMsg = detailStr.toLowerCase().includes("permission");
        if (hasPermissionMsg) {
          const errorMsg = tRef.current(
            "assistant.subscriptionUpgradeRequired",
            "Subscription Upgrade Required: You do not have permission to access this AI Assistant feature under your current plan. To upgrade your subscription tier, please contact support at tech-support@elelem.ai.",
          );
          showApiError(errorMsg);
          const err = new Error(errorMsg);
          err.isPermissionError = true;
          throw err;
        }
        throw new Error("Request failed");
      }

      const data = await restRes.json();
      handleRestPayload(data);
    };

    const shouldUseStream = !isDebugRest();

    try {
      if (!shouldUseStream) {
        await executeRestFallback();
        return;
      }

      let res;
      try {
        res = await fetch(`${BACKEND_URL}/ai-assistant/response`, {
          credentials: "include",
          method: "POST",
          signal: abortSignal,
          headers: {
            "Content-Type": "application/json",
            "Accept-Language": langCode,
          },
          body: JSON.stringify({
            user_prompt: hasText ? text : defaultAttachmentPrompt,
            attachments: currentAttachments.length > 0 ? currentAttachments : undefined,
            conversation_id: conversationIdRef.current || undefined,
            stream: true,
          }),
        });
      } catch (fetchErr) {
        if (fetchErr.name === "AbortError") {
          throw fetchErr;
        }
        console.warn("SSE fetch failed with network error:", fetchErr);
        throw fetchErr;
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detailObj = typeof errData.detail === "object" && errData.detail !== null ? errData.detail : {};
        const errCode = detailObj.code || errData.code;
        const detailStr = detailObj.message || (typeof errData.detail === "string" ? errData.detail : "");

        const isQuotaExceeded = errCode === "quota_exceeded" || detailStr.toLowerCase().includes("quota") || detailStr.includes("クォータ");
        if (isQuotaExceeded) {
          const quotaMsg = tRef.current(
            "assistant.quotaExceeded",
            "AI Quota Exceeded: Your organization has reached its monthly AI usage quota. Please contact your administrator to upgrade your plan.",
          );
          showApiError(quotaMsg);
          const err = new Error(quotaMsg);
          err.isQuotaError = true;
          throw err;
        }

        if (errCode === "response_in_progress" || (res.status === 429 && !isQuotaExceeded)) {
          const progressMsg = detailStr || tRef.current(
            "assistant.responseInProgress",
            "A response is currently being generated. Please wait a moment.",
          );
          showApiError(progressMsg);
          const err = new Error(progressMsg);
          err.isConcurrencyError = true;
          throw err;
        }

        const hasPermissionMsg = detailStr.toLowerCase().includes("permission");
        if (hasPermissionMsg) {
          const errorMsg = tRef.current(
            "assistant.subscriptionUpgradeRequired",
            "Subscription Upgrade Required: You do not have permission to access this AI Assistant feature under your current plan. To upgrade your subscription tier, please contact support at tech-support@elelem.ai.",
          );
          showApiError(errorMsg);
          const err = new Error(errorMsg);
          err.isPermissionError = true;
          throw err;
        }

        // Only fall back to REST when the endpoint rejected the stream request before processing (405 / 415)
        if (res.status === 405 || res.status === 415) {
          console.warn(`SSE endpoint returned HTTP ${res.status}, falling back to REST`);
          await executeRestFallback();
          return;
        }

        const err = new Error(detailStr || `Request failed with status ${res.status}`);
        err.status = res.status;
        throw err;
      }

      const isStreamResponse =
        res.headers?.get?.("content-type")?.includes("text/event-stream") &&
        typeof res.body?.getReader === "function";

      if (isStreamResponse) {
        // Mount bot message immediately in the chat log
        setMessages((prev) => [
          ...prev,
          {
            id: botMsgId,
            sender: "bot",
            text: "",
            animate: false,
            reasoning_steps: [],
            thoughts: [],
            is_streaming: true,
            startedAt: requestStartedAt,
            sources: [],
            suggestions: [],
          },
        ]);
        setIsBotTyping(false);
        setIsStreaming(true);
        isBusyRef.current = true;
        activeStreamTurnIdRef.current = botMsgId;

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let streamedText = "";
        let streamedSuggestions = [];
        let streamedReasoningSteps = [];
        let streamedThoughts = [];
        let streamedTurnElapsedMs = null;
        let currentEventType = "message";

        const updateStreamingMessage = (
          textVal,
          stepsVal,
          thoughtsVal,
          suggestionsVal,
          turnElapsedVal,
          isStreamingVal = true,
        ) => {
          setMessages((prev) => {
            const existingIdx = prev.findIndex((m) => m.id === botMsgId);
            if (existingIdx !== -1) {
              const updated = [...prev];
              updated[existingIdx] = {
                ...updated[existingIdx],
                text: textVal,
                animate: false,
                reasoning_steps: stepsVal,
                thoughts: thoughtsVal,
                is_streaming: isStreamingVal,
                startedAt: updated[existingIdx].startedAt || requestStartedAt,
                ...(turnElapsedVal !== undefined ? { turn_elapsed_ms: turnElapsedVal } : {}),
                ...(suggestionsVal ? { suggestions: suggestionsVal } : {}),
              };
              return updated;
            }
            return [
              ...prev,
              {
                id: botMsgId,
                sender: "bot",
                text: textVal,
                animate: false,
                reasoning_steps: stepsVal,
                thoughts: thoughtsVal,
                is_streaming: isStreamingVal,
                startedAt: requestStartedAt,
                turn_elapsed_ms: turnElapsedVal || null,
                sources: [],
                suggestions: suggestionsVal || [],
              },
            ];
          });
        };

        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            if (cancelledTurnIdsRef.current.has(botMsgId)) return;
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split("\n");
            buffer = lines.pop();

            for (const line of lines) {
              const trimmed = line.trim();
              if (!trimmed || trimmed.startsWith(":")) continue;
              if (trimmed.startsWith("event:")) {
                currentEventType = trimmed.slice(6).trim();
              } else if (trimmed.startsWith("data:")) {
                const dataStr = trimmed.slice(5).trim();
                let payload = null;
                try {
                  payload = JSON.parse(dataStr);
                } catch (jsonErr) {
                  // Ignore partial json parse error during chunk transport
                  continue;
                }

                if (!payload || typeof payload !== "object") continue;

                if (currentEventType === "init" || payload.conversation_id || payload.turn_id) {
                  if (payload.turn_id) {
                    activeServerTurnIdRef.current = payload.turn_id;
                  }
                  const cid = payload.conversation_id;
                  if (cid && conversationIdRef.current !== cid) {
                    const isNewConversation = conversationIdRef.current !== cid;
                    if (isNewConversation) {
                      isNewConversationRef.current = true;
                    }
                    conversationIdRef.current = cid;
                    setLocalStorageWithExpiry(
                      C.ELELEM_ASSISTANT_CONVERSATION_ID_KEY,
                      cid,
                      C.EXPIRY_TIME_IN_MINUTES,
                    );
                    if (isSpotlightMode && isNewConversation) {
                      fetchConversations();
                    }
                  }
                }

                if (currentEventType === "thought") {
                  const thObj = {
                    step: payload.step || (streamedThoughts.length + 1),
                    title: payload.title || "",
                    body: payload.body || payload.content || "",
                  };
                  streamedThoughts = [
                    ...streamedThoughts.filter((th) => th.step !== thObj.step),
                    thObj,
                  ];
                  updateStreamingMessage(
                    streamedText,
                    streamedReasoningSteps,
                    streamedThoughts,
                    undefined,
                    streamedTurnElapsedMs,
                  );
                } else if (currentEventType === "tool_start") {
                  streamedReasoningSteps = [
                    ...streamedReasoningSteps,
                    {
                      tool_call_id: payload.tool_call_id,
                      step: payload.step,
                      tool_name: payload.tool_name,
                      tool_args: payload.tool_args,
                      status: "running",
                    },
                  ];
                  updateStreamingMessage(
                    streamedText,
                    streamedReasoningSteps,
                    streamedThoughts,
                    undefined,
                    streamedTurnElapsedMs,
                  );
                } else if (currentEventType === "tool_end") {
                  streamedReasoningSteps = streamedReasoningSteps.map((s) => {
                    const matches = payload.tool_call_id
                      ? s.tool_call_id === payload.tool_call_id
                      : s.tool_name === payload.tool_name && s.status === "running";
                    return matches
                      ? {
                          ...s,
                          status: payload.status || "completed",
                          duration_ms: payload.duration_ms,
                          execution_time_ms: payload.duration_ms,
                          tool_output_preview: payload.tool_output_preview,
                          tool_output_truncated: payload.tool_output_truncated,
                        }
                      : s;
                  });
                  updateStreamingMessage(
                    streamedText,
                    streamedReasoningSteps,
                    streamedThoughts,
                    undefined,
                    streamedTurnElapsedMs,
                  );
                } else if (currentEventType === "chunk") {
                  streamedText += payload.content || "";
                  updateStreamingMessage(
                    streamedText,
                    streamedReasoningSteps,
                    streamedThoughts,
                    undefined,
                    streamedTurnElapsedMs,
                  );
                } else if (currentEventType === "done") {
                  streamedText = payload.response || streamedText;
                  streamedSuggestions = Array.isArray(payload.suggestions)
                    ? payload.suggestions
                    : [];
                  if (
                    Array.isArray(payload.reasoning_steps) &&
                    payload.reasoning_steps.length > 0
                  ) {
                    streamedReasoningSteps = payload.reasoning_steps;
                  }
                  if (Array.isArray(payload.thoughts) && payload.thoughts.length > 0) {
                    streamedThoughts = payload.thoughts;
                  }
                  if (typeof payload.turn_elapsed_ms === "number") {
                    streamedTurnElapsedMs = payload.turn_elapsed_ms;
                  }
                  updateStreamingMessage(
                    streamedText,
                    streamedReasoningSteps,
                    streamedThoughts,
                    streamedSuggestions,
                    streamedTurnElapsedMs,
                    false,
                  );
                  isBusyRef.current = false;
                  setIsStreaming(false);
                  setIsBotTyping(false);
                  triggerAiUsageRefresh();

                  // Immediately refresh and update Chat History on completion
                  const cid = payload.conversation_id || conversationIdRef.current;
                  const doneTitle = (payload.title || "").trim();
                  const isNewConv = isNewConversationRef.current;
                  if (cid) {
                    setConversations((prev) => {
                      const exists = prev.some((c) => c.conversation_id === cid);
                      if (!exists) {
                        return [
                          {
                            conversation_id: cid,
                            title: doneTitle || "New Conversation",
                            created_at: new Date().toISOString(),
                            updated_at: new Date().toISOString(),
                            message_count: 2,
                          },
                          ...prev,
                        ];
                      }
                      return prev.map((c) =>
                        c.conversation_id === cid
                          ? { ...c, title: (doneTitle && doneTitle !== "New Conversation") ? doneTitle : c.title }
                          : c
                      );
                    });
                  }

                  // Only call fetchConversations immediately if this is an established conversation.
                  // For a new conversation, fetching immediately would overwrite doneTitle with stale DB placeholder.
                  if (!isNewConv) {
                    fetchConversations();
                  }

                  // Requirement 8 / P2-a: Poll for new conversations and stop once the title changes
                  if (cid && isNewConv) {
                    isNewConversationRef.current = false;
                    let titleResolved = false;
                    const pollingTimers = [];
                    [2000, 5000, 10000].forEach((delay) => {
                      const timerId = setTimeout(async () => {
                        if (titleResolved) return;
                        try {
                          const res = await fetch(`${BACKEND_URL}/ai-assistant/conversations`, {
                            credentials: "include",
                          });
                          if (res.ok) {
                            const list = await res.json();
                            const target = Array.isArray(list) ? list.find((c) => c.conversation_id === cid) : null;
                            if (
                              target &&
                              target.title &&
                              target.title.trim() !== "New Conversation" &&
                              target.title.trim() !== doneTitle
                            ) {
                              titleResolved = true;
                              pollingTimers.forEach(clearTimeout);
                              setConversations(list.filter((c) => !deletedConvIdsRef.current.has(c.conversation_id)));
                            }
                          }
                        } catch (e) {
                          // ignore polling error
                        }
                      }, delay);
                      pollingTimers.push(timerId);
                    });
                  }
                } else if (currentEventType === "title") {
                  const titleText = (payload.title || "").trim();
                  const cid = payload.conversation_id || conversationIdRef.current;
                  if (titleText && cid) {
                    setConversations((prev) =>
                      prev.map((c) =>
                        c.conversation_id === cid ? { ...c, title: titleText } : c
                      )
                    );
                  }
                } else if (currentEventType === "error") {
                  throw new Error(payload.content || "Streaming error");
                }
              }
            }
          }
        } catch (streamErr) {
          throw streamErr;
        } finally {
          if (activeStreamTurnIdRef.current === botMsgId || currentRequestId === requestIdRef.current) {
            if (activeStreamTurnIdRef.current === botMsgId) {
              activeStreamTurnIdRef.current = null;
            }
            isBusyRef.current = false;
            setIsStreaming(false);
            setIsBotTyping(false);
          }
        }
      } else {
        const data = await res.json();
        handleRestPayload(data);
      }
    } catch (err) {
      if (err.name === "AbortError" || cancelledTurnIdsRef.current.has(botMsgId)) {
        if (activeStreamTurnIdRef.current === botMsgId) {
          activeStreamTurnIdRef.current = null;
        }
        isBusyRef.current = false;
        setIsStreaming(false);
        setIsBotTyping(false);
        return;
      }
      console.error("elelem Assistant request error:", err);
      if (activeStreamTurnIdRef.current === botMsgId || (!activeStreamTurnIdRef.current && currentRequestId === requestIdRef.current)) {
        activeStreamTurnIdRef.current = null;
        isBusyRef.current = false;
        setIsStreaming(false);
        setIsBotTyping(false);
      }
      const isQuotaErr =
        err.isQuotaError ||
        (err.message &&
          (err.message.toLowerCase().includes("quota") ||
            err.message.includes("クォータ")));
      const isPermissionErr =
        err.isPermissionError ||
        (err.message &&
          (err.message.includes("Subscription Upgrade Required") ||
            err.message.includes("サブスクリプションのアップグレード")));
      const isConcurrencyErr = err.isConcurrencyError;
      const isNetworkOr5xx =
        !isQuotaErr &&
        !isPermissionErr &&
        !isConcurrencyErr &&
        err.name !== "AbortError";

      // On a network error or 5xx: check if the backend already finished and saved the turn
      if (isNetworkOr5xx && conversationIdRef.current) {
        try {
          const checkRes = await fetch(
            `${BACKEND_URL}/ai-assistant/conversations/${conversationIdRef.current}`,
            { credentials: "include" },
          );
          if (checkRes.ok) {
            const detail = await checkRes.json();
            if (Array.isArray(detail?.messages) && detail.messages.length > 0) {
              const formatted = formatHistoryMessages(detail.messages);
              const lastMsg = formatted[formatted.length - 1];
              if (lastMsg && lastMsg.sender === "bot" && lastMsg.text) {
                setMessages(formatted);
                return;
              }
            }
          }
        } catch (checkErr) {
          // Fall through to show error with Retry action
        }
      }

      const errorMsg = isQuotaErr
        ? tRef.current(
            "assistant.quotaExceeded",
            "AI Quota Exceeded: Your organization has reached its monthly AI usage quota. Please contact your administrator to upgrade your plan.",
          )
        : isPermissionErr
        ? tRef.current(
            "assistant.subscriptionUpgradeRequired",
            "Subscription Upgrade Required: You do not have permission to access this AI Assistant feature under your current plan. To upgrade your subscription tier, please contact support at tech-support@elelem.ai.",
          )
        : isConcurrencyErr
        ? err.message
        : tRef.current(
            "assistant.errorMessage",
            "Sorry, I encountered an error. Please try again.",
          );

      const retryPromptText = hasText ? text : defaultAttachmentPrompt;

      setMessages((prev) => {
        const errorTurn = {
          id: botMsgId,
          sender: "bot",
          text: errorMsg,
          animate: false,
          isError: true,
          isRetryable: isNetworkOr5xx,
          retryPrompt: isNetworkOr5xx ? retryPromptText : undefined,
          retryAttachments: isNetworkOr5xx ? currentAttachments : undefined,
        };
        const existingIdx = prev.findIndex((m) => m.id === botMsgId);
        if (existingIdx !== -1) {
          const updated = [...prev];
          updated[existingIdx] = errorTurn;
          return updated;
        }
        return [...prev, errorTurn];
      });
    }
  };

  const handleCompleteTypewriter = useCallback((id) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, animate: false } : msg)),
    );
  }, []);

  const handleStopStreaming = useCallback(async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    const stoppedTurnId = activeStreamTurnIdRef.current;
    const serverTurnId = activeServerTurnIdRef.current;
    if (activeStreamTurnIdRef.current) {
      cancelledTurnIdsRef.current.add(activeStreamTurnIdRef.current);
      activeStreamTurnIdRef.current = null;
    }
    isBusyRef.current = false;
    setIsStreaming(false);
    setIsBotTyping(false);

    // 1. Mark only the message whose id is the stopped turn id as stopped in local state (P1-a)
    setMessages((prev) =>
      prev.map((m) =>
        m.id === stoppedTurnId || (m.is_streaming && !stoppedTurnId)
          ? {
              ...m,
              is_streaming: false,
              isStreaming: false,
              stopped: true,
            }
          : m,
      ),
    );

    // 2. Call POST /cancel {turn_id} which waits up to 5s for lock release and returns saved turn (Requirement 5)
    const activeCid = conversationIdRef.current;
    if (activeCid) {
      try {
        const cancelUrl = `${BACKEND_URL}/ai-assistant/conversations/${activeCid}/cancel`;
        const res = await fetch(cancelUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ turn_id: serverTurnId || stoppedTurnId || "" }),
        });
        if (res.ok) {
          const detail = await res.json();
          if (
            conversationIdRef.current === activeCid &&
            Array.isArray(detail?.messages) &&
            detail.messages.length > 0
          ) {
            const formatted = formatHistoryMessages(detail.messages);
            setMessages((prev) => {
              if (conversationIdRef.current !== activeCid) return prev;
              const stoppedIdx = prev.findIndex((m) => m.id === stoppedTurnId);
              if (stoppedIdx === -1) return prev;

              // Replace stopped turn's local AI message ONLY when server has an AI message after that user message
              // Never replace or remove local messages that the server copy doesn't contain yet!
              const serverAIMsg = formatted.slice().reverse().find((m) => m.sender === "bot");
              if (!serverAIMsg || formatted.length < 2) {
                return prev;
              }

              const prevUserTurns = prev.filter((m) => m.sender === "user").length;
              const serverUserTurns = formatted.filter((m) => m.sender === "user").length;
              if (serverUserTurns < prevUserTurns) {
                return prev;
              }

              return prev.map((m) => {
                if (m.id === stoppedTurnId) {
                  return {
                    ...m,
                    text: serverAIMsg.text || m.text,
                    reasoning_steps:
                      Array.isArray(serverAIMsg.reasoning_steps) && serverAIMsg.reasoning_steps.length > 0
                        ? serverAIMsg.reasoning_steps
                        : m.reasoning_steps,
                    thoughts:
                      Array.isArray(serverAIMsg.thoughts) && serverAIMsg.thoughts.length > 0
                        ? serverAIMsg.thoughts
                        : m.thoughts,
                    stopped: Boolean(serverAIMsg.stopped),
                    is_streaming: false,
                  };
                }
                return m;
              });
            });
          }
        }
      } catch (err) {
        // Fetch failed: local state already has user message and stopped bubble
      }
    }
  }, [BACKEND_URL, formatHistoryMessages]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (isBusy || isBusyRef.current) {
        return;
      }
      sendMessage(inputValue);
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      {!isOpen && !isSpotlightMode && (
        <button
          type="button"
          ref={fabRef}
          className="elelem-assistant-fab"
          onClick={() => setIsOpen(true)}
          aria-label={t("assistant.fabLabel", "Open elelem Assistant")}
          aria-haspopup="dialog"
          data-tooltip={t("assistant.fabTooltip", "Ask elelem Assistant")}
          data-testid="copilot-fab"
        >
          <img
            src="https://cdn.geo.elelem.ai/elelem-ai-assistant-logo.png"
            alt="elelem AI"
            className="elelem-assistant-fab-icon"
          />
          <span className="elelem-assistant-fab-badge">AI</span>
        </button>
      )}

      {/* Sliding Sidebar */}
      <div
        ref={isSpotlightMode && !isOpen ? spotlightContainerRef : undefined}
        role={isSpotlightMode && !isOpen ? undefined : "dialog"}
        aria-modal={isSpotlightMode && !isOpen ? undefined : "true"}
        aria-labelledby={isSpotlightMode && !isOpen ? undefined : "elelem-assistant-title"}
        aria-label={isSpotlightMode && !isOpen ? t("assistant.quickInput", "elelem Assistant Quick Input") : undefined}
        aria-hidden={isSpotlightMode ? undefined : !isOpen}
        inert={!isSpotlightMode && !isOpen ? true : undefined}
        className={`elelem-assistant-sidebar ${isOpen ? "open" : ""} ${isSpotlightMode ? "spotlight" : ""} ${isResizing ? "is-resizing" : ""}`}
        style={
          !isSpotlightMode
            ? {
                width: `min(${sidebarWidth}px, 100vw)`,
                maxWidth: "100vw",
                right: isOpen ? 0 : `max(-${sidebarWidth + 40}px, -100vw)`,
              }
            : undefined
        }
        data-testid="copilot-sidebar"
      >
        {/* Left Resizer Handle for Sidebar Drawer (Sidebar Mode Only) */}
        {!isSpotlightMode && isOpen && (
          <div
            className="elelem-assistant-resizer sidebar-resizer"
            onMouseDown={handleSidebarResizeStart}
            onDoubleClick={handleSidebarResetWidth}
            onKeyDown={handleSidebarResizerKeyDown}
            tabIndex={0}
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={sidebarWidth}
            aria-valuemin={C.MIN_ASSISTANT_SIDEBAR_WIDTH}
            aria-valuemax={C.MAX_ASSISTANT_SIDEBAR_WIDTH}
            aria-label={t("assistant.resizeSidebar", "Resize assistant sidebar")}
            title={t("assistant.resizeHint", "Drag to resize, double-click to reset")}
            data-testid="copilot-sidebar-resizer"
          />
        )}

        {/* Left History Sidebar (Spotlight Mode Only when open) */}
        {isSpotlightMode && isOpen && (
          <>
            <div
              className={`elelem-assistant-history-sidebar ${!isHistoryOpen ? "collapsed" : ""}`}
              style={isHistoryOpen ? { width: `${historyWidth}px` } : undefined}
              aria-hidden={!isHistoryOpen}
              inert={!isHistoryOpen ? true : undefined}
              data-testid="copilot-history-sidebar"
            >
            <div className="elelem-assistant-history-header">
              <span className="elelem-assistant-history-title">
                <MessageSquare size={14} aria-hidden="true" />
                <span>{t("assistant.chatHistory", "Chat History")}</span>
              </span>
              <button
                type="button"
                className="elelem-assistant-history-action-btn"
                onClick={() => setIsHistoryOpen(false)}
                title={t("assistant.hideHistory", "Hide history")}
                aria-label={t("assistant.hideHistory", "Hide history")}
                data-testid="copilot-hide-history-btn"
              >
                <PanelLeftClose size={15} aria-hidden="true" />
              </button>
            </div>

            <div className="elelem-assistant-history-new-chat-wrapper">
              <button
                type="button"
                className="elelem-assistant-history-new-chat-btn"
                onClick={handleRestart}
                disabled={isRestarting || isBotTyping || isHydrating}
                data-testid="copilot-history-new-chat-btn"
              >
                {isRestarting ? (
                  <Loader2 size={15} className="elelem-assistant-spin" aria-hidden="true" />
                ) : (
                  <Plus size={15} aria-hidden="true" />
                )}
                <span>{t("assistant.newChat", "New chat")}</span>
              </button>
            </div>

            <div className="elelem-assistant-history-list">
              {conversationsLoading ? (
                <div
                  className="elelem-assistant-history-loading"
                  data-testid="copilot-history-loading"
                >
                  <Loader2 size={16} className="elelem-assistant-spin" aria-hidden="true" />
                </div>
              ) : historyGroups.length === 0 ? (
                <div className="elelem-assistant-history-empty">
                  {t("assistant.noHistory", "No previous conversations")}
                </div>
              ) : (
                historyGroups.map((group) => (
                  <div key={group.key} className="elelem-assistant-history-group">
                    <div className="elelem-assistant-history-group-title">{group.label}</div>
                    <div className="elelem-assistant-history-items">
                      {group.items.map((conv) => {
                        const isEditing = editingConversationId === conv.conversation_id;
                        const isActive = conversationIdRef.current === conv.conversation_id;
                        return (
                          <div
                            key={conv.conversation_id}
                            className={`elelem-assistant-history-item ${isActive ? "active" : ""}`}
                            onClick={() => !isEditing && selectConversation(conv.conversation_id)}
                            onKeyDown={(e) => {
                              if (!isEditing && (e.key === "Enter" || e.key === " ")) {
                                if (e.target === e.currentTarget) {
                                  e.preventDefault();
                                  selectConversation(conv.conversation_id);
                                }
                              }
                            }}
                            role={isEditing ? undefined : "button"}
                            tabIndex={isEditing ? undefined : 0}
                            aria-current={isActive ? "true" : undefined}
                            aria-label={conv.title}
                            data-testid={`copilot-history-item-${conv.conversation_id}`}
                          >
                            {isEditing ? (
                              <div
                                className="elelem-assistant-history-edit-row"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="text"
                                  className="elelem-assistant-history-edit-input"
                                  value={editingTitle}
                                  onChange={(e) => setEditingTitle(e.target.value)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSaveRename(e, conv.conversation_id);
                                    if (e.key === "Escape") setEditingConversationId(null);
                                  }}
                                  autoFocus
                                  data-testid={`copilot-rename-input-${conv.conversation_id}`}
                                />
                                <button
                                  type="button"
                                  className="elelem-assistant-history-action-btn check-btn"
                                  onClick={(e) => handleSaveRename(e, conv.conversation_id)}
                                  title={t("common.save", "Save")}
                                  aria-label={t("common.save", "Save")}
                                  data-testid={`copilot-save-rename-btn-${conv.conversation_id}`}
                                >
                                  <Check size={13} aria-hidden="true" />
                                </button>
                              </div>
                            ) : (
                              <>
                                <div className="elelem-assistant-history-item-label" title={conv.title}>
                                  <MessageSquare size={13} className="elelem-assistant-history-item-icon" aria-hidden="true" />
                                  <span>{conv.title}</span>
                                </div>
                                <div className="elelem-assistant-history-item-actions">
                                  <button
                                    type="button"
                                    className="elelem-assistant-history-action-btn"
                                    onClick={(e) => handleStartRename(e, conv)}
                                    title={t("common.edit", "Rename")}
                                    aria-label={t("common.edit", "Rename")}
                                    data-testid={`copilot-rename-btn-${conv.conversation_id}`}
                                  >
                                    <Edit2 size={12} aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    className="elelem-assistant-history-action-btn delete-btn"
                                    onClick={(e) => handleDeleteConversation(e, conv.conversation_id)}
                                    title={t("common.delete", "Delete")}
                                    aria-label={t("common.delete", "Delete")}
                                    data-testid={`copilot-delete-btn-${conv.conversation_id}`}
                                  >
                                    <Trash2 size={12} aria-hidden="true" />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          {isHistoryOpen && (
            <div
              className="elelem-assistant-resizer history-resizer"
              onMouseDown={handleHistoryResizeStart}
              onDoubleClick={handleHistoryResetWidth}
              onKeyDown={handleHistoryResizerKeyDown}
              tabIndex={0}
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={historyWidth}
              aria-valuemin={C.MIN_ASSISTANT_HISTORY_WIDTH}
              aria-valuemax={C.MAX_ASSISTANT_HISTORY_WIDTH}
              aria-label={t("assistant.resizeHistory", "Resize chat history panel")}
              title={t("assistant.resizeHint", "Drag to resize, double-click to reset")}
              data-testid="copilot-history-resizer"
            />
          )}
        </>
      )}

        {/* Main Content Workspace */}
        <div className="elelem-assistant-main-workspace">
          {/* Header */}
          {(!isSpotlightMode || isOpen) && (
            <div className="elelem-assistant-header">
              <div className="elelem-assistant-brand-wrapper">
                {isSpotlightMode && isOpen && !isHistoryOpen && (
                  <button
                    type="button"
                    className="elelem-assistant-toggle-history-btn"
                    onClick={() => setIsHistoryOpen(true)}
                    title={t("assistant.showHistory", "Show history")}
                    aria-label={t("assistant.showHistory", "Show history")}
                    data-testid="copilot-show-history-btn"
                  >
                    <PanelLeft size={16} aria-hidden="true" />
                  </button>
                )}
                {logoFailed ? (
                  <div className="elelem-assistant-avatar">
                    <Sparkles size={16} className="elelem-assistant-avatar-icon" />
                  </div>
                ) : (
                  <img
                    src={ELELEM_LOGO_URL}
                    alt=""
                    aria-hidden="true"
                    className="elelem-assistant-logo"
                    onError={() => setLogoFailed(true)}
                  />
                )}
                <span
                  id="elelem-assistant-title"
                  className="elelem-assistant-brand"
                >
                  {t("assistant.title", "Assistant")}
                </span>
              </div>
            <div className="elelem-assistant-actions">
              <div
                className="elelem-assistant-overflow-wrapper"
                ref={overflowMenuRef}
              >
                <button
                  type="button"
                  className={`elelem-assistant-action-btn overflow-btn ${showOverflowMenu ? "active" : ""}`}
                  onClick={() => setShowOverflowMenu((prev) => !prev)}
                  aria-label={t("assistant.options", "Options")}
                  title={t("assistant.options", "Options")}
                  aria-expanded={showOverflowMenu}
                  data-testid="copilot-overflow-btn"
                >
                  <MoreHorizontal size={16} aria-hidden="true" />
                </button>
                {showOverflowMenu && (
                  <div
                    className="elelem-assistant-overflow-dropdown"
                    data-testid="copilot-overflow-dropdown"
                    role="menu"
                  >
                    <div className="elelem-assistant-overflow-header">
                      <span className="elelem-assistant-overflow-heading">
                        {t("assistant.options", "Options")}
                      </span>
                    </div>
                    <div className="elelem-assistant-overflow-content">
                      <label
                        className="elelem-assistant-overflow-row"
                        htmlFor="copilot-show-steps-switch"
                      >
                        <div className="elelem-assistant-overflow-text">
                          <span className="elelem-assistant-overflow-label">
                            {t("assistant.showAiSteps", "Show AI steps")}
                          </span>
                          <span className="elelem-assistant-overflow-desc">
                            {t(
                              "assistant.showStepsTooltip",
                              "See what the assistant checks while it works",
                            )}
                          </span>
                        </div>
                        <div className="elelem-assistant-switch">
                          <input
                            id="copilot-show-steps-switch"
                            type="checkbox"
                            role="switch"
                            checked={showSteps}
                            onChange={toggleShowSteps}
                            aria-label={t("assistant.showAiSteps", "Show AI steps")}
                            data-testid="copilot-show-steps-toggle"
                          />
                          <span className="elelem-assistant-slider" aria-hidden="true" />
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="elelem-assistant-action-btn"
                onClick={handleRestart}
                disabled={isRestarting || isHydrating}
                aria-label={t("assistant.restart", "Restart conversation")}
                data-testid="copilot-restart-btn"
              >
                <RotateCcw size={16} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="elelem-assistant-action-btn mode-toggle-btn"
                onClick={toggleAssistantMode}
                disabled={isHydrating}
                aria-label={
                  isSpotlightMode
                    ? t("assistant.minimize", "Switch to Sidebar Mode")
                    : t("assistant.maximize", "Switch to Spotlight Mode")
                }
                title={
                  isSpotlightMode
                    ? t("assistant.minimize", "Switch to Sidebar Mode")
                    : t("assistant.maximize", "Switch to Spotlight Mode")
                }
                data-testid="copilot-mode-toggle-btn"
              >
                {isSpotlightMode ? (
                  <Minimize2 size={16} aria-hidden="true" />
                ) : (
                  <Maximize2 size={16} aria-hidden="true" />
                )}
              </button>
              {isSpotlightMode && (
                <button
                  type="button"
                  className={`elelem-assistant-action-btn studio-toggle-btn ${isStudioOpen ? "active" : ""}`}
                  onClick={toggleStudio}
                  disabled={isHydrating}
                  aria-label={
                    isStudioOpen
                      ? t("assistant.studio.hideStudio", "Hide GEO Studio")
                      : t("assistant.studio.showStudio", "Show GEO Studio")
                  }
                  title={
                    isStudioOpen
                      ? t("assistant.studio.hideStudio", "Hide GEO Studio")
                      : t("assistant.studio.showStudio", "Show GEO Studio")
                  }
                  data-testid="copilot-studio-toggle-btn"
                >
                  <Activity size={15} aria-hidden="true" />
                  <span className="studio-toggle-label">
                    {t("assistant.studio.title", "GEO Studio")}
                  </span>
                </button>
              )}
              <div
                className="elelem-assistant-notification-wrapper"
                ref={notificationDropdownRef}
              >
                <button
                  type="button"
                  className="elelem-assistant-action-btn notification-btn"
                  onClick={toggleNotifications}
                  aria-label={t("topbar.notifications", "Notifications")}
                  title={t("topbar.notifications", "Notifications")}
                  data-testid="copilot-notification-btn"
                >
                  <Bell size={16} aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span
                      className="elelem-assistant-notification-badge"
                      data-testid="copilot-notification-badge"
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>

                {showNotifications && (
                  <div
                    className="elelem-assistant-notification-dropdown"
                    data-testid="copilot-notification-dropdown"
                  >
                    <div className="elelem-assistant-notification-header">
                      <h6 className="mb-0">
                        {t("topbar.notifications", "Notifications")}
                      </h6>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          className="elelem-assistant-notification-mark-all-btn"
                          onClick={markAllAsRead}
                          data-testid="copilot-notification-mark-all-btn"
                        >
                          {t("topbar.markAllAsRead", "Mark all as read")}
                        </button>
                      )}
                    </div>

                    <div className="elelem-assistant-notification-list">
                      {isLoadingNotifications ? (
                        <div className="elelem-assistant-notification-loading">
                          <Loader2 size={16} className="elelem-assistant-spin" aria-hidden="true" />
                          <span>{t("topbar.loading", "Loading...")}</span>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="elelem-assistant-notification-empty">
                          <BellOff size={22} className="mb-1 text-muted" aria-hidden="true" />
                          <p className="mb-0 text-muted">
                            {t("topbar.noNotificationsYet", "No notifications yet")}
                          </p>
                        </div>
                      ) : (
                        notifications.map((notification) => (
                          <div
                            key={notification.notification_id}
                            className={`elelem-assistant-notification-item ${
                              !notification.is_seen ? "unread" : ""
                            }`}
                            onClick={() => {
                              if (!notification.is_seen) {
                                markNotificationAsSeen(notification.notification_id);
                              }
                            }}
                            data-testid={`copilot-notification-item-${notification.notification_id}`}
                          >
                            <p className="elelem-assistant-notification-msg mb-1">
                              {notification.message}
                            </p>
                            <div className="elelem-assistant-notification-footer">
                              <small className="text-muted">
                                {formatNotificationTime(
                                  notification.created_at,
                                  t,
                                  i18n.resolvedLanguage || i18n.language,
                                )}
                              </small>
                              <button
                                type="button"
                                className="elelem-assistant-notification-discuss-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!notification.is_seen) {
                                    markNotificationAsSeen(
                                      notification.notification_id,
                                    );
                                  }
                                  sendMessage(
                                    t("topbar.discussNotificationPrompt", {
                                      message: notification.message,
                                      defaultValue: `Please review this notification and analyze the results: "${notification.message}"`,
                                    }),
                                  );
                                  setShowNotifications(false);
                                }}
                                data-testid={`copilot-discuss-notification-${notification.notification_id}`}
                              >
                                <Sparkles size={11} aria-hidden="true" />
                                <span>
                                  {t(
                                    "topbar.discussWithAssistant",
                                    "Discuss with AI Assistant",
                                  )}
                                </span>
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="elelem-assistant-action-btn close-btn"
                onClick={() => {
                  setIsOpen(false);
                  setShowSpotlightSuggestions(false);
                }}
                aria-label={t("assistant.close", "Close assistant")}
                data-testid="copilot-close-btn"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          </div>
        )}

        {/* Messages Area */}
        {(!isSpotlightMode || isOpen) && (
          <div
            className="elelem-assistant-messages-container"
            ref={messagesContainerRef}
            onScroll={handleMessagesScroll}
            data-testid="copilot-messages-container"
          >
          <div
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            aria-label={t("assistant.chatMessages", "Chat messages")}
            className="elelem-assistant-messages"
          >
            {messages.map((msg, msgIdx) => (
              <div
                key={msg.id}
                className={`elelem-assistant-message ${msg.sender}`}
                data-testid={`copilot-message-${msg.sender}`}
              >
                {msg.sender === "bot" ? (
                  <>
                    {/* Activity timeline / Reasoning steps accordion */}
                    {(Boolean(msg.is_streaming) ||
                      Boolean(msg.stopped) ||
                      (msg.reasoning_steps && msg.reasoning_steps.length > 0) ||
                      (msg.thoughts && msg.thoughts.length > 0)) && (
                      <ReasoningStepsAccordion
                        steps={msg.reasoning_steps}
                        thoughts={msg.thoughts}
                        turnElapsedMs={msg.turn_elapsed_ms}
                        isStreaming={Boolean(msg.is_streaming)}
                        isStopped={Boolean(msg.stopped)}
                        startedAt={msg.startedAt}
                        hasAnswer={Boolean(msg.text && msg.text.trim().length > 0)}
                        showSteps={showSteps}
                        t={t}
                      />
                    )}

                    {msg.stopped &&
                    (!msg.text ||
                      !msg.text.trim() ||
                      msg.text === "[Response stopped by the user before completion]") ? (
                      <div className="elelem-assistant-stopped-notice" data-testid="copilot-stopped-notice">
                        {t("assistant.responseStopped", "Response stopped.")}
                      </div>
                    ) : msg.animate ? (
                      <TypewriterText
                        id={msg.id}
                        text={msg.text}
                        onComplete={handleCompleteTypewriter}
                      />
                    ) : (
                      <ReactMarkdownWithGFMPlugin>
                        {sanitizeAssistantContent(msg.text)}
                      </ReactMarkdownWithGFMPlugin>
                    )}

                    {/* Retry button for retryable errors */}
                    {msg.isError && msg.isRetryable && (
                      <div className="elelem-assistant-error-actions">
                        <button
                          type="button"
                          className="elelem-assistant-retry-btn"
                          onClick={() => sendMessage(msg.retryPrompt, msg.retryAttachments)}
                          data-testid="copilot-retry-btn"
                        >
                          <RotateCcw size={13} aria-hidden="true" />
                          <span>{t("assistant.retry", "Retry")}</span>
                        </button>
                      </div>
                    )}

                    {/* Sources rendering */}
                    {msg.sources && msg.sources.length > 0 && (
                      <div
                        className="elelem-assistant-sources"
                        data-testid="copilot-sources"
                      >
                        {msg.sources.map((src, sIdx) => (
                          <div
                            key={sIdx}
                            className="elelem-assistant-source-item"
                          >
                            {src.url ? (
                              <a
                                href={src.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="elelem-assistant-source-link"
                              >
                                {src.title || t("assistant.source", "Source")} &rarr;
                              </a>
                            ) : (
                              <span className="elelem-assistant-source-title">
                                {src.title || t("assistant.source", "Source")}
                              </span>
                            )}
                            {src.text && (
                              <span className="elelem-assistant-source-snippet">
                                {src.text.slice(0, 100)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Suggested follow-up prompts (only surfaced on the latest bot turn) */}
                    {(() => {
                      const activeSuggestions =
                        msg.id === "welcome"
                          ? (Array.isArray(msg.suggestions) && msg.suggestions.length > 0
                              ? msg.suggestions
                              : (Array.isArray(starterSuggestions) && starterSuggestions.length > 0
                                  ? starterSuggestions
                                  : getDefaultStarterSuggestions(t)))
                          : msg.suggestions;

                      // Deduplicate follow-ups if already rendered inside an AssistantChartCard (Bug 10)
                      const hasChartFollowUps =
                        typeof msg.text === "string" &&
                        msg.text.includes("geo-chart") &&
                        msg.text.includes("followUps");

                      if (
                        !activeSuggestions ||
                        activeSuggestions.length === 0 ||
                        msgIdx !== messages.length - 1 ||
                        msg.animate ||
                        hasChartFollowUps
                      ) {
                        return null;
                      }

                      return (
                        <div
                          className="elelem-assistant-suggestions"
                          data-testid="copilot-suggestions"
                        >
                          <span className="elelem-assistant-suggestions-label">
                            <Sparkles size={11} aria-hidden="true" />
                            {t("assistant.suggestedQueries", "Suggested")}
                          </span>
                          <div className="elelem-assistant-suggestions-list">
                            {activeSuggestions.map((suggestion, sugIdx) => (
                              <button
                                type="button"
                                key={sugIdx}
                                className="elelem-assistant-suggestion-chip"
                                onClick={() => sendMessage(suggestion)}
                                disabled={isBusy}
                                data-testid="copilot-suggestion-chip"
                              >
                                <span>{suggestion}</span>
                                <ArrowUpRight size={12} aria-hidden="true" />
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}

                    {/* Turn limit recovery action */}
                    {isTurnLimitMessage(msg.text) && (
                      <div
                        className="elelem-assistant-turn-limit-action"
                        data-testid="copilot-turn-limit-action"
                      >
                        <button
                          type="button"
                          className="elelem-assistant-turn-limit-btn"
                          onClick={handleRestart}
disabled={isRestarting || isBotTyping || isHydrating}
                          data-testid="copilot-turn-limit-restart-btn"
                        >
                          <Plus size={13} aria-hidden="true" />
                          <span>{t("assistant.startNewChat", "Start a new chat")}</span>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {msg.attachments && msg.attachments.length > 0 && (
                      <div className="elelem-assistant-msg-attachments">
                        {msg.attachments.map((att, aIdx) => {
                          const downloadUrl =
                            att.download_url ||
                            (att.file_id
                              ? `/ai-assistant/download/${att.file_id}`
                              : null);
                          return (
                            <div
                              key={aIdx}
                              className={`elelem-assistant-msg-attachment-chip ${downloadUrl ? "clickable" : ""}`}
                              onClick={
                                downloadUrl
                                  ? () =>
                                      downloadAssistantFile(
                                        downloadUrl,
                                        att.file_name,
                                      )
                                  : undefined
                              }
                              title={
                                downloadUrl
                                  ? t(
                                      "assistant.downloadAttachment",
                                      "Click to download",
                                    )
                                  : undefined
                              }
                              role={downloadUrl ? "button" : undefined}
                              tabIndex={downloadUrl ? 0 : undefined}
                              onKeyDown={
                                downloadUrl
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        downloadAssistantFile(
                                          downloadUrl,
                                          att.file_name,
                                        );
                                      }
                                    }
                                  : undefined
                              }
                            >
                              {att.mime_type?.startsWith("image/") ? (
                                <ImageIcon size={12} />
                              ) : (
                                <FileText size={12} />
                              )}
                              <span>{att.file_name}</span>
                              {downloadUrl && (
                                <Download
                                  size={11}
                                  className="elelem-assistant-chip-download-icon"
                                  aria-hidden="true"
                                />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {msg.text}
                  </>
                )}
              </div>
            ))}

            {isBotTyping && (
              <div
                className="elelem-assistant-message bot"
                data-testid="copilot-typing-indicator"
                role="status"
                aria-label={t("assistant.typing", "Assistant is typing")}
              >
                <div
                  className="elelem-assistant-typing-dots"
                  aria-hidden="true"
                >
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

        {/* Spotlight Floating Suggestion Pills */}
        {isSpotlightMode &&
          !isOpen &&
          showSpotlightSuggestions &&
          !hasStartedConversation && (
            <div
              className="elelem-assistant-spotlight-suggestions"
              data-testid="copilot-spotlight-suggestions"
            >
              {(starterSuggestions.length > 0
                ? starterSuggestions.slice(0, 3)
                : getDefaultStarterSuggestions(t)
              ).map((suggestion, sIdx) => (
                <button
                  type="button"
                  key={sIdx}
                  className="elelem-assistant-spotlight-suggestion-pill"
                  disabled={isBusy}
                  onClick={() => {
                    setShowSpotlightSuggestions(false);
                    sendMessage(suggestion);
                  }}
                  data-testid={`copilot-spotlight-suggestion-pill-${sIdx}`}
                >
                  <span>{suggestion}</span>
                </button>
              ))}
            </div>
          )}

        {/* Input Bar */}
        <div className="elelem-assistant-input-area">
          {/* Attachments Preview Row */}
          {(attachedFiles.length > 0 || isUploadingFile || uploadError) && (
            <div className="elelem-assistant-attachments-preview">
              {attachedFiles.map((file, idx) => (
                <div key={idx} className="elelem-assistant-attachment-chip">
                  {file.mime_type?.startsWith("image/") ? (
                    <ImageIcon size={13} />
                  ) : (
                    <FileText size={13} />
                  )}
                  <span className="elelem-assistant-chip-name">{file.file_name}</span>
                  <button
                    type="button"
                    onClick={() => removeAttachment(idx)}
                    className="elelem-assistant-chip-remove"
                    aria-label={t("assistant.removeAttachment", {
                      fileName: file.file_name,
                      defaultValue: `Remove ${file.file_name}`,
                    })}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              {isUploadingFile && (
                <div className="elelem-assistant-attachment-chip uploading">
                  <Loader2 size={13} className="elelem-assistant-spin" />
                  <span>{t("assistant.uploading", "Uploading...")}</span>
                </div>
              )}
              {uploadError && (
                <div className="elelem-assistant-attachment-error">
                  {uploadError}
                </div>
              )}
            </div>
          )}

          <div className="elelem-assistant-input-row">
            <button
              type="button"
              className="elelem-assistant-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
              title={t("assistant.attachTooltip", "Attach files (PDF, images, documents, spreadsheets, text, data)")}
              aria-label={t("assistant.attachLabel", "Attach file")}
              data-testid="copilot-attach-btn"
            >
              <Paperclip size={16} aria-hidden="true" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileSelect}
              multiple
              accept="image/*,application/pdf,.csv,.docx,.xlsx,.txt,.md,.markdown,.json,.xml"
              data-testid="copilot-file-input"
            />
            <textarea
              ref={textareaRef}
              className="elelem-assistant-input"
              placeholder={placeholderText}
              aria-label={t("assistant.messageInput", "Message input")}
              rows={1}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onClick={() => {
                handleSpotlightInputClick(true);
              }}
              onFocus={() => {
                if (preventFocusOpenRef.current) return;
                justFocusedRef.current = true;
                if (justFocusedTimerRef.current) {
                  clearTimeout(justFocusedTimerRef.current);
                }
                justFocusedTimerRef.current = setTimeout(() => {
                  justFocusedRef.current = false;
                  justFocusedTimerRef.current = null;
                }, 200);
                handleSpotlightInputClick(false);
              }}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              disabled={isRestarting || isHydrating}
              aria-disabled={isBusy}
              data-testid="copilot-input"
            />
            {isStreaming ? (
              <button
                type="button"
                className="elelem-assistant-send-btn stop-btn"
                onClick={handleStopStreaming}
                aria-label={t("assistant.stopGenerating", "Stop generating")}
                title={t("assistant.stopGenerating", "Stop generating")}
                data-testid="copilot-stop-btn"
              >
                <Square size={13} fill="currentColor" aria-hidden="true" />
              </button>
            ) : (
              <button
                type="button"
                className="elelem-assistant-send-btn"
                onClick={() => sendMessage(inputValue)}
                disabled={isBusy || (!inputValue.trim() && attachedFiles.length === 0)}
                aria-label={t("assistant.sendMessage", "Send message")}
                data-testid="copilot-send-btn"
              >
                <Send size={16} aria-hidden="true" />
              </button>
            )}
          </div>
          <div
            className="elelem-assistant-disclaimer"
            data-testid="copilot-disclaimer"
          >
            {t(
              "assistant.disclaimer",
              "elelem Assistant is an experimental feature and can make mistakes.",
            )}
          </div>
        </div>
      </div>

      {/* Right GEO Studio Panel (Spotlight Workspace Only) */}
      {isSpotlightMode && isOpen && (
        <>
          {isStudioOpen && (
            <div
              className="elelem-assistant-resizer studio-resizer"
              onMouseDown={handleStudioResizeStart}
              onDoubleClick={handleStudioResetWidth}
              onKeyDown={handleStudioResizerKeyDown}
              tabIndex={0}
              role="separator"
              aria-orientation="vertical"
              aria-valuenow={studioWidth}
              aria-valuemin={C.MIN_ASSISTANT_STUDIO_WIDTH}
              aria-valuemax={C.MAX_ASSISTANT_STUDIO_WIDTH}
              aria-label={t("assistant.resizeStudio", "Resize GEO Studio panel")}
              title={t("assistant.resizeHint", "Drag to resize, double-click to reset")}
              data-testid="copilot-studio-resizer"
            />
          )}
          <div
            className={`elelem-assistant-studio-panel ${!isStudioOpen ? "collapsed" : ""}`}
            style={isStudioOpen ? { width: `${studioWidth}px` } : undefined}
            aria-hidden={!isStudioOpen}
            inert={!isStudioOpen ? true : undefined}
            data-testid="copilot-studio-panel"
          >
            {hasOpenedStudio && (
              <ErrorBoundary>
                <Suspense fallback={null}>
                  <GeoStudio
                    isOpen={isStudioOpen}
                    onClose={handleCloseStudio}
                    onTriggerPrompt={sendMessage}
                    onExpandStudioWidth={handleExpandStudioToHalfScreen}
                  />
                </Suspense>
              </ErrorBoundary>
            )}
          </div>
        </>
      )}
    </div>
  </>
);
}
